// Host side of the Bitreel Graphic Patterns Viewer. Maps the Design Your Own
// barrel's selection onto the viewer's event API and owns the iframe, the
// message channel, and the capture round trip. The contract is
// CLIENT_INTEGRATION.md in bitreel-graphic-patterns-viewer.
import { imagebossUrl } from '@Helpers/imageboss';
import {
  DEFAULT_LEVEL_INDEX,
  ETCH_TARGETS,
  OPACITY_LEVELS,
  PRINT_MODES,
} from '@Helpers/pattern-recolor';

const PREFIX = 'bitreel-graphic-patterns';

export const VIEWER_EVENTS = Object.freeze({
  setConfig: `${PREFIX}-set-config`,
  setPatternImage: `${PREFIX}-set-pattern-image`,
  captureImage: `${PREFIX}-capture-image`,
  ready: `${PREFIX}-ready`,
  configChanged: `${PREFIX}-config-changed`,
  imageCaptured: `${PREFIX}-image-captured`,
  captureFailed: `${PREFIX}-capture-failed`,
  error: `${PREFIX}-error`,
  viewChanged: `${PREFIX}-view-changed`,
});

// Optional events a deployed viewer lists in its ready message.
export const VIEWER_CAPABILITIES = Object.freeze({
  patternImage: 'set-pattern-image',
  capture: 'capture-image',
});

// Long edge of the panel texture. A 3000x6000 full panel becomes 1024x2048:
// power of two and well under mobile texture limits.
export const TEXTURE_HEIGHT = 2048;

const CAPTURE_TIMEOUT_MS = 15000;

// Through ImageBoss, never raw S3, because the viewer uploads it as a WebGL
// texture and needs CORS headers.
export const textureUrlFor = image => {
  if (!image?.src) return null;
  const height = image.height
    ? Math.min(image.height, TEXTURE_HEIGHT)
    : TEXTURE_HEIGHT;
  return imagebossUrl(image.src, { height });
};

const colorFor = hex =>
  hex ? { hex, alpha: 100, clear: false } : { clear: true };

// A selection carries the preset's alpha; the viewer takes I–IV as 1–4 and
// applies its own strengths. An alpha off the preset list is the default
// level rather than the lightest one.
const levelFor = alpha => {
  const index = OPACITY_LEVELS.findIndex(level => level.alpha === alpha);
  return (index === -1 ? DEFAULT_LEVEL_INDEX : index) + 1;
};

// The viewer's set-config payload for a graphicPatternDesignerUpdated detail.
export const viewerConfigFrom = detail => {
  const selection = detail.selection || {};
  const etched = selection.mode === PRINT_MODES.ETCHED_GLASS;
  return {
    material: detail.material === 'varia' ? 'varia' : 'glass',
    designOption: etched ? 'etched-glass' : 'digital-print',
    // Etching has no color; clear reads as frost in the viewer.
    patternColor: etched ? { clear: true } : colorFor(selection.foreground),
    patternOpacity: levelFor(selection.foregroundAlpha),
    backgroundColor: etched ? { clear: true } : colorFor(selection.background),
    backgroundOpacity: levelFor(selection.backgroundAlpha),
  };
};

// The library full panel is the 48x96 panel drawn at the variant's own
// scale, so it maps onto the viewer's panel at repeat 1. The viewer's own
// scale (tiling) is never sent. Null when the variant has no full panel.
export const viewerPatternImageFrom = detail => {
  const image = detail.variant?.full_panel;
  const url = textureUrlFor(image);
  if (!url) return null;

  const selection = detail.selection || {};
  const etchBackground =
    selection.mode === PRINT_MODES.ETCHED_GLASS &&
    selection.etchTarget === ETCH_TARGETS.BACKGROUND;

  // Viewer alpha is the ink; a knock-out file or an etched background flips
  // it, and both together cancel out.
  return {
    url,
    repeat: 1,
    invert: !!image.alpha_is_ground !== etchBackground,
  };
};

const samePatternImage = (a, b) =>
  !!a &&
  !!b &&
  a.url === b.url &&
  a.repeat === b.repeat &&
  a.invert === b.invert;

let captureCounter = 0;

export class BitreelViewerClient {
  constructor({ url }) {
    this.url = url;
    this.origin = new URL(url, window.location.href).origin;
    this.iframe = null;
    // idle | loading | ready
    this.status = 'idle';
    this.version = null;
    this.capabilities = [];
    this.lastError = null;
    // True from sending a pattern image until the viewer reports it on the
    // panel (config-changed carries the loaded image) or fails.
    this.patternImageLoading = false;
    this._latest = null;
    this._sentPatternImage = null;
    this._flushHandle = 0;
    this._captures = new Map();
    this._listeners = new Set();
    this._viewListeners = new Set();
    this._onMessage = this._onMessage.bind(this);
  }

  get attached() {
    return !!this.iframe;
  }

  // Pins the viewer to this page: it then only takes commands from, and
  // replies to, this origin.
  get src() {
    const src = new URL(this.url, window.location.href);
    src.searchParams.set('origin', window.location.origin);
    return src.href;
  }

  supports(capability) {
    return this.capabilities.includes(capability);
  }

  onChange(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  // Orbit, zoom, reset and fullscreen gestures inside the iframe, for
  // analytics; viewers before 1.2.0 never send them.
  onViewChanged(listener) {
    this._viewListeners.add(listener);
    return () => this._viewListeners.delete(listener);
  }

  // Listener before src: the viewer posts ready as soon as it starts.
  attach(iframe) {
    if (this.iframe) return;
    this.iframe = iframe;
    this.status = 'loading';
    window.addEventListener('message', this._onMessage);
    iframe.src = this.src;
  }

  // Keeps only the latest selection. Sent once the viewer is ready, and at
  // most once per frame while a color picker is being dragged.
  update({ config, patternImage }) {
    this._latest = { config, patternImage };
    if (this.status !== 'ready' || this._flushHandle) return;
    this._flushHandle = window.requestAnimationFrame(() => {
      this._flushHandle = 0;
      this._flush();
    });
  }

  send(type, detail) {
    if (!this.iframe?.contentWindow) return;
    this.iframe.contentWindow.postMessage({ type, detail }, this.origin);
  }

  // Resolves with a Blob of the viewer's current frame.
  capture({ format = 'png', background = 'canvas', scale = 2 } = {}) {
    if (this.status !== 'ready') {
      return Promise.reject(new Error('3D viewer is not ready'));
    }
    if (!this.supports(VIEWER_CAPABILITIES.capture)) {
      return Promise.reject(new Error('3D viewer cannot capture images'));
    }

    const requestId = `walter-${++captureCounter}`;
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this._captures.delete(requestId);
        reject(new Error('3D viewer capture timed out'));
      }, CAPTURE_TIMEOUT_MS);
      this._captures.set(requestId, { resolve, reject, timer });
      this.send(VIEWER_EVENTS.captureImage, {
        requestId,
        format,
        background,
        scale,
      });
    });
  }

  dispose() {
    window.removeEventListener('message', this._onMessage);
    if (this._flushHandle) window.cancelAnimationFrame(this._flushHandle);
    this._flushHandle = 0;
    this._captures.forEach(({ reject, timer }) => {
      window.clearTimeout(timer);
      reject(new Error('3D viewer was removed'));
    });
    this._captures.clear();
    this._listeners.clear();
    this._viewListeners.clear();
    this.iframe = null;
    this.status = 'idle';
  }

  _notify() {
    this._listeners.forEach(listener => listener(this));
  }

  _flush() {
    if (!this._latest || this.status !== 'ready') return;
    const { config, patternImage } = this._latest;

    if (
      patternImage &&
      this.supports(VIEWER_CAPABILITIES.patternImage) &&
      !samePatternImage(patternImage, this._sentPatternImage)
    ) {
      this._sentPatternImage = patternImage;
      this.patternImageLoading = true;
      this.send(VIEWER_EVENTS.setPatternImage, patternImage);
      this._notify();
    }

    this.send(VIEWER_EVENTS.setConfig, config);
  }

  _settlePatternImage() {
    if (!this.patternImageLoading) return;
    this.patternImageLoading = false;
    this._notify();
  }

  _settleCapture(requestId, error, blob) {
    const pending = this._captures.get(requestId);
    if (!pending) return;
    this._captures.delete(requestId);
    window.clearTimeout(pending.timer);
    if (error) pending.reject(new Error(error));
    else pending.resolve(blob);
  }

  _onMessage(event) {
    if (event.origin !== this.origin) return;
    if (!this.iframe || event.source !== this.iframe.contentWindow) return;

    const { type, detail } = event.data || {};
    switch (type) {
      case VIEWER_EVENTS.ready:
        // Also fires again if the viewer reloads, so start the sync over.
        this.version = detail?.version || null;
        this.capabilities = Array.isArray(detail?.capabilities)
          ? detail.capabilities
          : [];
        this.status = 'ready';
        this._sentPatternImage = null;
        this.patternImageLoading = false;
        this._flush();
        this._notify();
        break;
      case VIEWER_EVENTS.configChanged:
        if (samePatternImage(detail?.patternImage, this._sentPatternImage)) {
          this._settlePatternImage();
        }
        break;
      case VIEWER_EVENTS.imageCaptured:
        this._settleCapture(detail?.requestId, null, detail?.blob);
        break;
      case VIEWER_EVENTS.captureFailed:
        this._settleCapture(
          detail?.requestId,
          detail?.error || '3D viewer capture failed'
        );
        break;
      case VIEWER_EVENTS.error:
        // The viewer keeps its last pattern; forget what was sent so the
        // next selection change retries the image.
        this._sentPatternImage = null;
        this.lastError = detail || null;
        this.patternImageLoading = false;
        this._notify();
        break;
      case VIEWER_EVENTS.viewChanged:
        this._viewListeners.forEach(listener => listener(detail || {}));
        break;
      default:
    }
  }
}
